import { T } from "./Translate";
import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { ConfirmationNumber as ConfirmationNumberIcon } from "../icons";
import { ContentCopy as ContentCopyIcon } from "../icons";
import { TimerOutlined as TimerOutlinedIcon } from "../icons";
import { doc, updateDoc, increment } from "firebase/firestore";
import { db } from "../firebase";
import { brandColors } from "../theme";
import { getCouponStatus } from "../components/CouponExpiryFields";

const formatExpiry = (ymd) => ymd.split("-").reverse().join("/");

const DAY_MS = 24 * 60 * 60 * 1000;
// Con menos de esto por delante se muestra la cuenta regresiva en vez de la fecha.
const COUNTDOWN_FROM_MS = 7 * DAY_MS;

// Mismo criterio que getCouponStatus: el cupón vale hasta el final de ese día.
const expiryTime = (ymd) => new Date(`${ymd}T23:59:59`).getTime();

const MAX_TIMEOUT_MS = 2 ** 31 - 1;

const pad = (n) => String(n).padStart(2, "0");
const formatCountdown = (ms) => {
  const totalSeconds = Math.max(Math.floor(ms / 1000), 0);
  const days = Math.floor(totalSeconds / 86400);
  const hh = pad(Math.floor((totalSeconds % 86400) / 3600));
  const mm = pad(Math.floor((totalSeconds % 3600) / 60));
  const ss = pad(totalSeconds % 60);
  return `${days > 0 ? `${days}d ` : ""}${hh}:${mm}:${ss}`;
};

// Reloj que solo avanza (cada segundo) mientras la cuenta regresiva está a la
// vista, para no re-renderizar cupones que vencen dentro de semanas.
const useCountdownNow = (expiresAt) => {
  const [now, setNow] = useState(() => Date.now());
  const end = expiresAt ? expiryTime(expiresAt) : null;
  const inCountdown = end !== null && now < end && end - now <= COUNTDOWN_FROM_MS;
  // Fuera de la ventana depende de `now` para reprogramar el despertador.
  const wakeKey = inCountdown ? 0 : now;

  useEffect(() => {
    if (end === null || Date.now() >= end) return undefined;
    if (!inCountdown) {
      // Despierta justo cuando entra en la ventana de cuenta regresiva
      // (setTimeout no acepta más de ~24 días: si falta más, reprograma).
      const delay = Math.min(end - COUNTDOWN_FROM_MS - Date.now() + 1000, MAX_TIMEOUT_MS);
      const wake = setTimeout(() => setNow(Date.now()), delay);
      return () => clearTimeout(wake);
    }
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [end, inCountdown, wakeKey]);

  return now;
};

// Cuenta un uso por dispositivo: copiar varias veces el mismo cupón no lo agota.
const markUsedOnce = (offerId) => {
  const key = `cuponUsed:${offerId}`;
  try {
    if (localStorage.getItem(key)) return false;
    localStorage.setItem(key, "1");
  } catch {
    // Sin localStorage contamos igual.
  }
  return true;
};

// Cupón estilo "ticket": ícono a la izquierda, separador fino, "CUPÓN" +
// código en la misma línea, botón Copiar a la derecha. Al copiar, el borde
// se pone verde un momento como confirmación.
// Con `offer` también muestra el vencimiento (cuponExpiresAt / cuponMaxUses),
// suma un uso en cuponUses al copiar y se deshabilita si venció o se agotó.
const CopyToClipboardButton = ({ textToCopy, offer }) => {
  const [isCopied, setIsCopied] = useState(false);
  const [uses, setUses] = useState(offer?.cuponUses || 0);
  // El reloj también hace que el cupón pase a "Vencido" en vivo al llegar a 0.
  const now = useCountdownNow(offer?.cuponExpiresAt);
  const remainingMs = offer?.cuponExpiresAt ? expiryTime(offer.cuponExpiresAt) - now : null;
  const showCountdown = remainingMs !== null && remainingMs > 0 && remainingMs <= COUNTDOWN_FROM_MS;
  const status = offer ? getCouponStatus({ ...offer, cuponUses: uses }) : "active";
  const isActive = status === "active";

  const copyToClipboard = () => {
    if (!isActive) return;
    navigator.clipboard.writeText(textToCopy);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
    if (offer?.id && offer.cuponMaxUses && markUsedOnce(offer.id)) {
      setUses((u) => u + 1);
      updateDoc(doc(db, "offers", offer.id), { cuponUses: increment(1) }).catch((error) =>
        console.error("Error al contar el uso del cupón:", error)
      );
    }
  };

  // De cuánto es el cupón: el % de la oferta o, si el cupón es de un monto
  // fijo (ej. "US$ 70" en AliExpress), el texto de cuponDescuento.
  const percent = Number(offer?.offer);
  const discountLabel =
    percent > 0 && percent < 100 ? `${percent}% OFF`
      : offer?.cuponDescuento ? `${offer.cuponDescuento} OFF`
      : null;

  const details = [];
  if (offer?.cuponExpiresAt && !showCountdown && remainingMs > 0) {
    details.push(<T key="date">Vence el {formatExpiry(offer.cuponExpiresAt)}</T>);
  }
  if (offer?.cuponMaxUses) {
    const left = Math.max(offer.cuponMaxUses - uses, 0);
    details.push(<T key="uses">Quedan {left} de {offer.cuponMaxUses} usos</T>);
  }

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
    <Box
      id="tour-cupon"
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        width: "100%",
        boxSizing: "border-box",
        borderRadius: "8px",
        boxShadow: isCopied ? "0 0 0 2px #4caf50" : "none",
        background: isActive
          ? `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.primaryHover} 100%)`
          : "#757575",
        opacity: isActive ? 1 : 0.8,
        color: "white",
        pl: "10px",
        pr: 0.5,
        py: "6px",
        gap: 1.5,
        overflow: "hidden",
        transition: "box-shadow 0.2s ease",
      }}
    >
      <ConfirmationNumberIcon fontSize="small" sx={{ flexShrink: 0 }} />

      {/* Separador estilo ticket: tres líneas verticales + dos surcos
          (semicírculos) mordiendo el borde de arriba y de abajo. */}
      <Box sx={{ display: "flex", gap: "3px", flexShrink: 0 }}>
        {[0, 1, 2].map((i) => (
          <Box key={i} sx={{ width: "2px", height: "18px", bgcolor: "rgba(255,255,255,0.35)" }} />
        ))}
      </Box>
      <Box
        sx={{
          position: "absolute",
          top: -7,
          left: "42px",
          width: 14,
          height: 14,
          borderRadius: "50%",
          bgcolor: brandColors.backgroundPaper,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -7,
          left: "42px",
          width: 14,
          height: 14,
          borderRadius: "50%",
          bgcolor: brandColors.backgroundPaper,
        }}
      />

      <Box sx={{ flexGrow: 1, minWidth: 0, display: "flex", alignItems: "baseline", gap: 1 }}>
        <Typography sx={{ color: "white", fontSize: "0.65rem", fontWeight: 700, letterSpacing: 1, opacity: 0.8, flexShrink: 0 }}>
          <T>CUPÓN</T>
        </Typography>
        <Typography
          sx={{
            color: "white",
            fontFamily: "monospace",
            fontWeight: 700,
            fontSize: "1.1rem",
            letterSpacing: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textDecoration: isActive ? "none" : "line-through",
          }}
        >
          {textToCopy}
        </Typography>
      </Box>

      {discountLabel && (
        <Typography
          sx={{
            flexShrink: 0,
            color: brandColors.primary,
            bgcolor: "white",
            borderRadius: "6px",
            px: 0.75,
            py: 0.25,
            fontSize: "0.75rem",
            fontWeight: 800,
            whiteSpace: "nowrap",
          }}
        >
          {discountLabel}
        </Typography>
      )}

      <Button
        id="tour-copiar"
        onClick={copyToClipboard}
        disabled={!isActive}
        startIcon={<ContentCopyIcon fontSize="small" />}
        sx={{
          color: "white",
          px: 2,
          flexShrink: 0,
          textTransform: "none",
          fontWeight: 600,
          "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
          "&.Mui-disabled": { color: "rgba(255,255,255,0.8)" },
        }}
      >
        {status === "expired" ? <T>Vencido</T>
          : status === "exhausted" ? <T>Agotado</T>
          : isCopied ? <T>Copiado</T> : <T>Copiar</T>}
      </Button>
    </Box>
    {showCountdown && isActive && (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          mt: 0.75,
          color: remainingMs <= DAY_MS ? "error.main" : "warning.main",
        }}
      >
        <TimerOutlinedIcon sx={{ fontSize: 18 }} />
        <Typography variant="body2" sx={{ fontWeight: 600, color: "inherit" }}>
          <T>Vence en</T>
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: "0.95rem", color: "inherit" }}
        >
          {formatCountdown(remainingMs)}
        </Typography>
      </Box>
    )}
    {details.length > 0 && (
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
        {details.reduce((acc, d, i) => (i ? [...acc, " · ", d] : [d]), [])}
      </Typography>
    )}
    </Box>
  );
};

export default CopyToClipboardButton;
